import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { hasActiveReferral } from "../../../infrastructure/utils/referralTracking";
import { fetchLoginKind } from "../../../application/actions/authActions";
import { getLoginKind } from "../../../application/selectors/auth";
import { resetAuthSession } from "../../../application/slices/auth";
import {
  consumeAuthRedirectReason,
  isAccessTokenExpired,
  readStoredUser,
} from "../../../infrastructure/utils/authSession";
import MobileLogin from "../../components/auth/MobileLogin";
import UserPassLogin from "../../components/auth/UserPassLogin";
import LoadingWrapper from "../../components/shared/loading";

const LoginPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const { loading, kind } = useSelector(getLoginKind);
  const [loginKind, setLoginKind] = useState("UserPass");
  const [sessionNotice, setSessionNotice] = useState("");

  const preferMobileLogin = useMemo(() => {
    const ref = new URLSearchParams(location.search).get("ref");
    return Boolean((ref || "").trim()) || hasActiveReferral();
  }, [location.search]);

  useEffect(() => {
    const storedUser = readStoredUser();
    if (storedUser?.accessToken && isAccessTokenExpired(storedUser)) {
      dispatch(resetAuthSession());
    }

    if (!window.globalConfig?.API_URL) {
      setSessionNotice(
        "آدرس API تنظیم نشده است. فایل public/envconfig.js را بررسی کنید."
      );
      return;
    }

    const redirectReason = consumeAuthRedirectReason();
    if (redirectReason) {
      setSessionNotice(
        "نشست قبلی منقضی شده است. لطفاً دوباره وارد شوید."
      );
      if (process.env.NODE_ENV === "development") {
        console.warn("[auth] session ended:", redirectReason);
      }
    }
  }, [dispatch]);

  const onGetLoginMethod = useCallback(() => {
    if (!kind) {
      dispatch(fetchLoginKind());
    }
  }, [dispatch, kind]);

  useEffect(() => {
    onGetLoginMethod();
  }, [onGetLoginMethod]);

  useEffect(() => {
    if (kind) {
      setLoginKind(preferMobileLogin ? "Mobile" : "UserPass");
    }
  }, [kind, preferMobileLogin]);

  const onChangeLoginKind = (newKind) => {
    setLoginKind(newKind);
  };

  if (loading && !loginKind) {
    return <LoadingWrapper />;
  }

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: { xs: 1, sm: 2 },
      }}
    >
      {sessionNotice ? (
        <Alert severity="info">{sessionNotice}</Alert>
      ) : null}
      {loginKind === "Mobile" ? (
        <MobileLogin hasBothLogin={true} onChangeLoginKind={onChangeLoginKind} />
      ) : (
        <UserPassLogin hasBothLogin={true} onChangeLoginKind={onChangeLoginKind} />
      )}
    </Box>
  );
};

export default LoginPage;
